import { useEffect, useState, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { Heart, Bone, Sun, Droplet, ChevronUp, Menu, X, Play, Camera, Video, Sparkles, PawPrint, Settings, Users, MessageCircle, Send } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "./components/ui/dialog";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import AdminPanel from "./components/AdminPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon mapping for fun facts
const iconMap = {
  heart: Heart,
  bone: Bone,
  sun: Sun,
  droplet: Droplet,
};

function App() {
  const [pet, setPet] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [funFacts, setFunFacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  
  // Visitor counter and comments
  const [visitorCount, setVisitorCount] = useState(0);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState({ author_name: "", message: "" });
  const [submittingComment, setSubmittingComment] = useState(false);

  const heroRef = useRef(null);
  const aboutRef = useRef(null);
  const galleryRef = useRef(null);
  const videosRef = useRef(null);
  const factsRef = useRef(null);
  const commentsRef = useRef(null);

  // Fetch all data
  const fetchData = async () => {
    try {
      // First, seed the data
      await axios.post(`${API}/seed`);
      
      // Increment visitor count
      const visitorRes = await axios.post(`${API}/visitors/increment`);
      setVisitorCount(visitorRes.data.count);
      
      // Then fetch all data
      const [petRes, photosRes, videosRes, factsRes, commentsRes] = await Promise.all([
        axios.get(`${API}/pet`),
        axios.get(`${API}/photos`),
        axios.get(`${API}/videos`),
        axios.get(`${API}/facts`),
        axios.get(`${API}/comments`),
      ]);
      
      setPet(petRes.data);
      setPhotos(photosRes.data);
      setVideos(videosRes.data);
      setFunFacts(factsRes.data);
      setComments(commentsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Scroll handler for scroll-to-top button
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: "smooth" });
    setMobileNavOpen(false);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Submit comment
  const submitComment = async (e) => {
    e.preventDefault();
    if (!newComment.author_name.trim() || !newComment.message.trim()) return;
    
    setSubmittingComment(true);
    try {
      const res = await axios.post(`${API}/comments`, newComment);
      setComments([res.data, ...comments]);
      setNewComment({ author_name: "", message: "" });
    } catch (error) {
      console.error("Error submitting comment:", error);
    } finally {
      setSubmittingComment(false);
    }
  };

  // Format date for comments
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Handle returning from admin panel
  const handleBackFromAdmin = () => {
    setShowAdmin(false);
    setLoading(true);
    fetchData(); // Refresh data after admin changes
  };

  // Show admin panel
  if (showAdmin) {
    return <AdminPanel onBack={handleBackFromAdmin} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFCF8]" data-testid="loading-screen">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#D4A373] animate-pulse flex items-center justify-center">
            <PawPrint className="w-8 h-8 text-white" />
          </div>
          <p className="font-heading text-xl text-[#2C3E2C]">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFCF8]" data-testid="pet-website">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-[#E5E5E5]" data-testid="navigation">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button 
            onClick={() => scrollToSection(heroRef)}
            className="font-heading text-2xl font-bold text-[#D4A373] hover:text-[#E76F51] transition-colors"
            data-testid="nav-logo"
          >
            {pet?.name || "Buddy"}
          </button>
          
          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            <button onClick={() => scrollToSection(aboutRef)} className="nav-link" data-testid="nav-about">About</button>
            <button onClick={() => scrollToSection(galleryRef)} className="nav-link" data-testid="nav-gallery">Gallery</button>
            <button onClick={() => scrollToSection(factsRef)} className="nav-link" data-testid="nav-facts">Fun Facts</button>
            <button onClick={() => scrollToSection(commentsRef)} className="nav-link" data-testid="nav-guestbook">Guestbook</button>
            <button 
              onClick={() => setShowAdmin(true)} 
              className="p-2 hover:bg-[#FAEDCD] rounded-full transition-colors"
              title="Admin Panel"
              data-testid="nav-admin"
            >
              <Settings className="w-5 h-5 text-[#2C3E2C]" />
            </button>
          </div>

          {/* Mobile Nav Toggle */}
          <div className="flex items-center gap-2 md:hidden">
            <button 
              onClick={() => setShowAdmin(true)} 
              className="p-2 hover:bg-[#FAEDCD] rounded-full transition-colors"
              data-testid="mobile-admin-btn"
            >
              <Settings className="w-5 h-5 text-[#2C3E2C]" />
            </button>
            <button 
              onClick={() => setMobileNavOpen(!mobileNavOpen)}
              className="p-2 text-[#2C3E2C]"
              data-testid="mobile-nav-toggle"
            >
              {mobileNavOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Nav Menu */}
      {mobileNavOpen && (
        <div className="mobile-nav" data-testid="mobile-nav-menu">
          <button onClick={() => scrollToSection(aboutRef)} className="text-2xl font-heading text-[#2C3E2C] hover:text-[#D4A373]">About</button>
          <button onClick={() => scrollToSection(galleryRef)} className="text-2xl font-heading text-[#2C3E2C] hover:text-[#D4A373]">Gallery</button>
          <button onClick={() => scrollToSection(factsRef)} className="text-2xl font-heading text-[#2C3E2C] hover:text-[#D4A373]">Fun Facts</button>
          <button onClick={() => scrollToSection(commentsRef)} className="text-2xl font-heading text-[#2C3E2C] hover:text-[#D4A373]">Guestbook</button>
        </div>
      )}

      {/* Hero Section */}
      <section ref={heroRef} className="min-h-screen flex items-center justify-center relative overflow-hidden pt-20" data-testid="hero-section">
        <div className="hero-title select-none" style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
          {pet?.name || "BUDDY"}
        </div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="text-center md:text-left animate-fade-in-up">
              <span className="font-accent text-2xl text-[#E76F51] rotate-[-3deg] inline-block mb-4" data-testid="hero-greeting">
                Meet our beloved
              </span>
              <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl font-black text-[#2C3E2C] leading-[0.9] mb-6" data-testid="hero-title">
                {pet?.name || "Buddy"}
              </h1>
              <p className="text-lg md:text-xl text-[#2C3E2C]/70 mb-4" data-testid="hero-breed">
                {pet?.breed || "Golden Retriever"} • {pet?.age || "3 years old"}
              </p>
              <p className="text-base text-[#2C3E2C]/60 max-w-md mx-auto md:mx-0 mb-8" data-testid="hero-bio">
                {pet?.bio?.substring(0, 120)}...
              </p>
              <button 
                onClick={() => scrollToSection(aboutRef)}
                className="btn-primary bg-[#D4A373] text-white rounded-full px-8 py-4 font-bold text-lg shadow-lg"
                data-testid="hero-cta"
              >
                Get to Know Me
              </button>
            </div>
            
            <div className="hero-image-container animate-fade-in-up delay-200">
              <div className="relative">
                <div className="absolute -inset-4 bg-[#A3B18A] blob-shape opacity-30 animate-float"></div>
                <img 
                  src={photos.find(p => p.is_featured)?.url || photos[0]?.url || "https://images.unsplash.com/photo-1705351738397-0557d8c23d13?crop=entropy&cs=srgb&fm=jpg&q=85"}
                  alt={pet?.name || "Buddy"}
                  className="relative rounded-3xl shadow-2xl w-full max-w-lg mx-auto object-cover aspect-[4/5]"
                  data-testid="hero-image"
                />
                <div className="absolute -bottom-4 -right-4 bg-white p-4 rounded-xl shadow-lg rotate-3">
                  <p className="font-accent text-xl text-[#E76F51]" data-testid="hero-tagline">Best friend ever!</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative paws */}
        <PawPrint className="floating-paw w-24 h-24 text-[#D4A373]" style={{ top: '10%', left: '5%', animationDelay: '0s' }} />
        <PawPrint className="floating-paw w-16 h-16 text-[#A3B18A]" style={{ bottom: '20%', right: '10%', animationDelay: '2s' }} />
      </section>

      {/* About Section */}
      <section ref={aboutRef} className="py-24 md:py-32 section-secondary" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="about-image-wrapper order-2 md:order-1">
              <img 
                src={photos[1]?.url || "https://images.unsplash.com/photo-1733264333877-e3c906cded37?crop=entropy&cs=srgb&fm=jpg&q=85"}
                alt={`${pet?.name || "Buddy"} relaxing`}
                className="rounded-3xl shadow-xl w-full object-cover aspect-square"
                data-testid="about-image"
              />
            </div>
            
            <div className="order-1 md:order-2">
              <span className="font-accent text-2xl text-[#E76F51] mb-2 inline-block" data-testid="about-label">A little about me</span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#2C3E2C] mb-6" data-testid="about-title">
                Meet {pet?.name || "Buddy"}
              </h2>
              <p className="text-lg text-[#2C3E2C]/70 leading-relaxed mb-8" data-testid="about-bio">
                {pet?.bio || "Buddy is our beloved family companion who brings endless joy to our home. With his golden coat and warm brown eyes, he captures everyone's heart."}
              </p>
              
              <div className="mb-8">
                <h3 className="font-heading text-xl font-semibold text-[#2C3E2C] mb-4" data-testid="traits-title">Personality Traits</h3>
                <div className="flex flex-wrap gap-2" data-testid="traits-list">
                  {(pet?.personality_traits || ["Friendly", "Playful", "Loyal", "Gentle"]).map((trait, index) => (
                    <span key={index} className="trait-badge" data-testid={`trait-${index}`}>{trait}</span>
                  ))}
                </div>
              </div>
              
              <div>
                <h3 className="font-heading text-xl font-semibold text-[#2C3E2C] mb-4" data-testid="favorites-title">Favorite Things</h3>
                <div className="flex flex-wrap gap-2" data-testid="favorites-list">
                  {(pet?.favorite_things || ["Tennis balls", "Belly rubs", "Swimming"]).map((thing, index) => (
                    <span key={index} className="trait-badge" data-testid={`favorite-${index}`}>{thing}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Photo Gallery Section */}
      <section ref={galleryRef} className="py-24 md:py-32" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="font-accent text-2xl text-[#E76F51] mb-2 inline-block">
              <Camera className="inline w-6 h-6 mr-2" />
              Captured moments
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#2C3E2C]" data-testid="gallery-title">
              Photo Gallery
            </h2>
          </div>
          
          {photos.length > 0 ? (
            <div className="gallery-grid" data-testid="gallery-grid">
              {photos.map((photo, index) => (
                <div 
                  key={photo.id} 
                  className="gallery-item"
                  onClick={() => setLightboxPhoto(photo)}
                  data-testid={`gallery-item-${index}`}
                >
                  <img src={photo.url} alt={photo.caption || `Photo ${index + 1}`} />
                  <div className="gallery-caption">{photo.caption}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-white/50 rounded-3xl" data-testid="photos-placeholder">
              <Camera className="w-16 h-16 mx-auto mb-4 text-[#D4A373]" />
              <p className="font-heading text-xl text-[#2C3E2C]/70">No photos yet!</p>
              <p className="text-[#2C3E2C]/50 mt-2">Click the settings icon to add photos</p>
            </div>
          )}
        </div>
      </section>

      {/* Photo Lightbox */}
      <Dialog open={!!lightboxPhoto} onOpenChange={() => setLightboxPhoto(null)}>
        <DialogContent className="max-w-4xl bg-black/95 border-none p-0" aria-describedby="lightbox-description" data-testid="lightbox-dialog">
          <DialogTitle className="sr-only">Photo View</DialogTitle>
          <p id="lightbox-description" className="sr-only">Viewing enlarged photo from gallery</p>
          {lightboxPhoto && (
            <div className="relative">
              <img 
                src={lightboxPhoto.url} 
                alt={lightboxPhoto.caption}
                className="w-full h-auto max-h-[85vh] object-contain"
                data-testid="lightbox-image"
              />
              {lightboxPhoto.caption && (
                <p className="absolute bottom-4 left-4 right-4 text-center font-accent text-2xl text-white" data-testid="lightbox-caption">
                  {lightboxPhoto.caption}
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Video Section */}
      <section ref={videosRef} className="py-24 md:py-32 section-muted" data-testid="videos-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="font-accent text-2xl text-[#E76F51] mb-2 inline-block">
              <Video className="inline w-6 h-6 mr-2" />
              In action
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#2C3E2C]" data-testid="videos-title">
              Video Memories
            </h2>
          </div>
          
          {videos.length > 0 ? (
            <div className="grid md:grid-cols-2 gap-8" data-testid="videos-grid">
              {videos.map((video, index) => (
                <div key={video.id} className="video-card" data-testid={`video-item-${index}`}>
                  <iframe 
                    src={video.url}
                    title={video.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-white/50 rounded-3xl" data-testid="videos-placeholder">
              <Play className="w-16 h-16 mx-auto mb-4 text-[#D4A373]" />
              <p className="font-heading text-xl text-[#2C3E2C]/70">Videos coming soon!</p>
              <p className="text-[#2C3E2C]/50 mt-2">Click the settings icon to add YouTube or Vimeo videos</p>
            </div>
          )}
        </div>
      </section>

      {/* Fun Facts Section */}
      <section ref={factsRef} className="py-24 md:py-32" data-testid="facts-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="font-accent text-2xl text-[#E76F51] mb-2 inline-block">
              <Sparkles className="inline w-6 h-6 mr-2" />
              Did you know?
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#2C3E2C]" data-testid="facts-title">
              Fun Facts About {pet?.name || "Buddy"}
            </h2>
          </div>
          
          {funFacts.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="facts-grid">
              {funFacts.map((fact, index) => {
                const IconComponent = iconMap[fact.icon] || Heart;
                return (
                  <div key={fact.id} className="fact-card" data-testid={`fact-card-${index}`}>
                    <div className="fact-icon">
                      <IconComponent className="w-6 h-6" />
                    </div>
                    <h3 className="font-heading text-xl font-semibold text-[#2C3E2C] mb-2" data-testid={`fact-title-${index}`}>
                      {fact.title}
                    </h3>
                    <p className="text-[#2C3E2C]/70" data-testid={`fact-description-${index}`}>
                      {fact.description}
                    </p>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16 bg-white/50 rounded-3xl" data-testid="facts-placeholder">
              <Sparkles className="w-16 h-16 mx-auto mb-4 text-[#D4A373]" />
              <p className="font-heading text-xl text-[#2C3E2C]/70">No fun facts yet!</p>
              <p className="text-[#2C3E2C]/50 mt-2">Click the settings icon to add fun facts</p>
            </div>
          )}
        </div>
      </section>

      {/* Guestbook / Comments Section */}
      <section ref={commentsRef} className="py-24 md:py-32 section-secondary" data-testid="comments-section">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="font-accent text-2xl text-[#E76F51] mb-2 inline-block">
              <MessageCircle className="inline w-6 h-6 mr-2" />
              Leave a message
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#2C3E2C]" data-testid="comments-title">
              Guestbook
            </h2>
            
            {/* Visitor Counter */}
            <div className="mt-6 inline-flex items-center gap-2 bg-white/80 px-6 py-3 rounded-full shadow-sm" data-testid="visitor-counter">
              <Users className="w-5 h-5 text-[#D4A373]" />
              <span className="text-[#2C3E2C] font-semibold">{visitorCount.toLocaleString()}</span>
              <span className="text-[#2C3E2C]/60">visitors</span>
            </div>
          </div>
          
          {/* Comment Form */}
          <div className="bg-white rounded-2xl p-8 shadow-sm mb-8" data-testid="comment-form">
            <h3 className="font-heading text-xl font-semibold text-[#2C3E2C] mb-4">
              Say hello to {pet?.name || "Buddy"}!
            </h3>
            <form onSubmit={submitComment} className="space-y-4">
              <div>
                <Input 
                  value={newComment.author_name}
                  onChange={(e) => setNewComment(prev => ({ ...prev, author_name: e.target.value }))}
                  placeholder="Your name"
                  className="border-[#E5E5E5] focus:border-[#D4A373]"
                  required
                  data-testid="comment-name-input"
                />
              </div>
              <div>
                <Textarea 
                  value={newComment.message}
                  onChange={(e) => setNewComment(prev => ({ ...prev, message: e.target.value }))}
                  placeholder={`Leave a message for ${pet?.name || "Buddy"}...`}
                  className="border-[#E5E5E5] focus:border-[#D4A373] min-h-[100px]"
                  required
                  data-testid="comment-message-input"
                />
              </div>
              <Button 
                type="submit" 
                disabled={submittingComment}
                className="bg-[#D4A373] hover:bg-[#C59262] text-white"
                data-testid="comment-submit-btn"
              >
                {submittingComment ? "Sending..." : <><Send className="w-4 h-4 mr-2" /> Send Message</>}
              </Button>
            </form>
          </div>
          
          {/* Comments List */}
          <div className="space-y-4" data-testid="comments-list">
            {comments.length > 0 ? (
              comments.map((comment, index) => (
                <div 
                  key={comment.id} 
                  className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-[#A3B18A]"
                  data-testid={`comment-${index}`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-semibold text-[#2C3E2C]" data-testid={`comment-author-${index}`}>
                      {comment.author_name}
                    </h4>
                    <span className="text-sm text-[#2C3E2C]/50" data-testid={`comment-date-${index}`}>
                      {formatDate(comment.created_at)}
                    </span>
                  </div>
                  <p className="text-[#2C3E2C]/70" data-testid={`comment-message-${index}`}>
                    {comment.message}
                  </p>
                </div>
              ))
            ) : (
              <div className="text-center py-12 bg-white/50 rounded-2xl">
                <MessageCircle className="w-12 h-12 mx-auto mb-4 text-[#D4A373]" />
                <p className="text-[#2C3E2C]/70">No messages yet. Be the first to say hello!</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#2C3E2C] text-white py-16 relative" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <PawPrint className="w-12 h-12 mx-auto mb-6 text-[#D4A373]" />
          <h3 className="font-heading text-3xl font-bold mb-4" data-testid="footer-title">
            {pet?.name || "Buddy"}'s Family
          </h3>
          <p className="text-white/70 max-w-md mx-auto mb-8" data-testid="footer-text">
            Thank you for visiting! {pet?.name || "Buddy"} brings so much love and happiness to our family every single day.
          </p>
          <div className="flex justify-center gap-4">
            <span className="font-accent text-xl text-[#D4A373]" data-testid="footer-tagline">
              Made with love for our furry friend
            </span>
          </div>
          <p className="text-white/50 text-sm mt-8" data-testid="footer-copyright">
            © {new Date().getFullYear()} The {pet?.name || "Buddy"} Archives
          </p>
        </div>
      </footer>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button 
          onClick={scrollToTop}
          className="btn-scroll-top"
          data-testid="scroll-top-btn"
          aria-label="Scroll to top"
        >
          <ChevronUp className="w-6 h-6" />
        </button>
      )}
    </div>
  );
}

export default App;
